import { QuestionItem } from './types';

/**
 * Evaluates if a question is visible based on conditional parent-child logic.
 * A question is visible if:
 * - It has no parent (parent_qit_id is null) → always visible
 * - Its parent is in a different group (not in `questions[]`) → trust backend `visible` flag
 * - Its parent has been answered, and either no specific choice is required (parent_choice_id is null)
 *   or the parent's answer matches parent_choice_id
 */
export function evaluateVisibility(
  question: QuestionItem,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  localAnswers: Record<number, any>,
  questions: QuestionItem[]
): boolean {
  if (!question.parent_qit_id) {
    return question.visible; // Root question: trust backend visibility flag
  }

  // Find the parent question
  const parent = questions.find((q) => q.qit_id === question.parent_qit_id);
  if (!parent) {
    return question.visible; // Cross-group parent: trust backend visibility
  }

  // localAnswers takes precedence over existing_answer
  const parentAnswer =
    question.parent_qit_id in localAnswers
      ? localAnswers[question.parent_qit_id]
      : parent.existing_answer;

  // Parent not answered → child hidden
  if (parentAnswer == null || parentAnswer === '') {
    return false;
  }

  // No specific choice required → visible when parent answered with anything
  if (!question.parent_choice_id) {
    return true;
  }

  // Check specific choice match
  // parentAnswer could be a number (single choice) or array (multiselect)
  if (Array.isArray(parentAnswer)) {
    return parentAnswer.includes(question.parent_choice_id);
  }
  return parentAnswer === question.parent_choice_id;
}

/**
 * Computes the ordered list of QIT IDs to present to the user.
 * Filters out: invisible questions, already-answered questions (this session),
 * previously answered questions (unless always_ask + ai_extracted), deferred questions.
 * Sorted by `order`.
 *
 * @param pendingFileQitIds - QIT IDs from the IPE's pending_file_qits list. A file
 *   QIT in this set has an existing answer but insufficient file uploads; it must be
 *   re-presented even though existing_answer is non-null.
 */
export function computeVisibleQuestionQueue(
  questions: QuestionItem[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  localAnswers: Record<number, any>,
  deferredQitIds: number[],
  pendingFileQitIds: number[] = [],
  onlyQitIds: number[] | null = null
): number[] {
  const deferredSet = new Set(deferredQitIds);
  const pendingFileSet = new Set(pendingFileQitIds);
  const onlySet = onlyQitIds ? new Set(onlyQitIds) : null;

  if (process.env.NODE_ENV !== 'production' && onlySet) {
    console.log(
      `[Q] filter  only=${Array.from(onlySet).join(',')}` +
        `  group=[${questions.length} ${questions[0]?.qit_id}-${
          questions[questions.length - 1]?.qit_id
        }]`
    );
  }

  return questions
    .filter((q) => {
      // 1. Must be visible (conditional logic)
      if (!evaluateVisibility(q, localAnswers, questions)) {
        if (process.env.NODE_ENV !== 'production' && onlySet) {
          console.log(`  [Q] X qit=${q.qit_id} reason=hidden`);
        }
        return false;
      }

      // 1b. When targeting specific QITs only (re-answer / file management mode),
      //     exclude every question not in the set — even if it is unanswered.
      if (onlySet && !onlySet.has(q.qit_id)) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`  [Q] X qit=${q.qit_id} reason=not-in-onlySet`);
        }
        return false;
      }

      // 2. Not deferred
      if (deferredSet.has(q.qit_id)) {
        if (process.env.NODE_ENV !== 'production' && onlySet) {
          console.log(`  [Q] X qit=${q.qit_id} reason=deferred`);
        }
        return false;
      }

      // 3. Not answered this session
      if (q.qit_id in localAnswers) {
        if (process.env.NODE_ENV !== 'production' && onlySet) {
          console.log(`  [Q] X qit=${q.qit_id} reason=localAnswer`);
        }
        return false;
      }

      // 4. Not previously answered UNLESS ...
      if (
        q.existing_answer != null &&
        q.existing_answer !== '' &&
        !(q.always_ask && q.ai_extracted) &&
        !(q.question_type === 'multiselect' && q.ai_extracted) &&
        !pendingFileSet.has(q.qit_id) &&
        !(onlySet && onlySet.has(q.qit_id))
      ) {
        if (process.env.NODE_ENV !== 'production' && onlySet) {
          console.log(
            `  [Q] X qit=${q.qit_id} reason=answered  existing=${JSON.stringify(
              q.existing_answer
            )}  alwaysAsk=${q.always_ask}  ai=${q.ai_extracted}  type=${
              q.question_type
            }  inPending=${pendingFileSet.has(q.qit_id)}  inOnly=${onlySet?.has(
              q.qit_id
            )}`
          );
        }
        return false;
      }

      if (process.env.NODE_ENV !== 'production' && onlySet) {
        console.log(`  [Q] ✓ qit=${q.qit_id} kept`);
      }

      return true;
    })
    .sort((a, b) => a.order - b.order)
    .map((q) => q.qit_id);
}
